import { Axios } from 'axios';
import { injectable } from 'inversify';

import { Analytics } from '@vp/common/core/Analytics';
import { Failure, OperationResult, Success } from '@vp/common/core/OperationResult';
import { ErrorExtractor } from '@vp/common/data/ErrorExtractor';
import { RemoveMediaDto } from '@vp/manager/gallery/core/dto/RemoveMediaDto';
import { UploadMediaDto } from '@vp/manager/gallery/core/dto/UploadMediaDto';
import { GalleryManagerRepository } from '@vp/manager/gallery/core/interface/GalleryManagerRepository';
import { GalleryManagerUploadHandle } from '@vp/manager/gallery/core/interface/GalleryManagerUploadHandle';
import { GalleryManagerDataMapper } from '@vp/manager/gallery/data/GalleryManagerDataMapper';
import { UploadStateMachine } from '@vp/manager/gallery/data/upload/UploadStateMachine';
import { ProfileLiveMediaModel } from '@vp/profile/core/model/ProfileLiveMediaModel';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfilePhotoModel } from '@vp/profile/core/model/ProfilePhotoModel';
import { ProfileResponseDto } from '@vp/profile/data/dto/ProfileResponseDto';
import { ProfileDataMapper } from '@vp/profile/data/ProfileDataMapper';

import { SwitchModeResponseDto } from './dto/SwitchModeResponseDto';

@injectable()
export class RestGalleryManagerRepository implements GalleryManagerRepository {
  private readonly ChunkSize: number = 5 * 1024 * 1024;

  constructor(
    private readonly http: Axios,
    private readonly profileDataMapper: ProfileDataMapper,
    private readonly galleryManagerMapper: GalleryManagerDataMapper,
    private readonly errorExtractor: ErrorExtractor,
    private readonly analytics: Analytics,
  ) {}

  async makeStatic(photoId: string, controller?: AbortController): Promise<OperationResult<[ProfilePhotoModel, ProfileLiveMediaModel]>> {
    try {
      const makeStaticDto = this.galleryManagerMapper.toMakeStaticRequestDto(photoId);

      const { data } = await this.http.put<SwitchModeResponseDto>('api/live_photos/switch_mode', makeStaticDto, {
        signal: controller?.signal,
      });

      const photo = this.profileDataMapper.toPhoto(data);
      const liveMedia = this.profileDataMapper.toLiveMedia(data.live_media);

      return Success.from([photo, liveMedia]);
    } catch (error) {
      this.analytics.trackError(error);
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }

  async makeLive(photoId: string, controller?: AbortController): Promise<OperationResult<[ProfilePhotoModel, ProfileLiveMediaModel]>> {
    try {
      const makeLiveDto = this.galleryManagerMapper.toMakeLiveRequestDto(photoId);

      const { data } = await this.http.put<SwitchModeResponseDto>('api/live_photos/switch_mode', makeLiveDto, {
        signal: controller?.signal,
      });

      const photo = this.profileDataMapper.toPhoto(data);
      const liveMedia = this.profileDataMapper.toLiveMedia(data.live_media);

      return Success.from([photo, liveMedia]);
    } catch (error) {
      this.analytics.trackError(error);
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }

  upload(dto: UploadMediaDto): GalleryManagerUploadHandle {
    const context = this.galleryManagerMapper.toUploadStateMachineContext(dto, this.ChunkSize);

    const machine = new UploadStateMachine(context, this.http, this.errorExtractor, this.galleryManagerMapper, this.analytics);
    machine.start();

    return machine;
  }

  async remove(dto: RemoveMediaDto, controller?: AbortController): Promise<OperationResult<ProfileModel>> {
    try {
      const formData = this.galleryManagerMapper.toRemoveFormData(dto);

      const { data } = await this.http.put<ProfileResponseDto>(`/api/profiles/${dto.profileId}/upload`, formData, {
        signal: controller?.signal,
      });

      return Success.from(this.profileDataMapper.toOwnerProfile(data));
    } catch (error) {
      this.analytics.trackError(error);
      const message = this.errorExtractor.extract(error);
      return Failure.from(message);
    }
  }
}
